import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  aceitarManutencao,
  rejeitarManutencao,
  prorrogarManutencao,
  apagarManutencao,
  suspenderOcorrencia,
  pausarSerie,
  retomarSerie,
  setPaginaManutencao,
  setPaginaManutencaoEscopo,
  setStatusFiltroManutencao,
  listarManutencoes,
} from "../../../features/manutencao/slices";
import { ManutencaoItem } from "../../../features/manutencao/services";
import BaseLayout from "../../../components/Layout";
import Button from "../../../components/Button";
import Modal from "../../../components/Modal";
import { getUser } from "../../../helpers/user";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Trash2,
} from "lucide-react";
import "./styles.scss";

const NIVEL_MINIMO_ACOES = 6;

const STATUS_OPCOES = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "APROVADA", label: "Aprovada" },
  { value: "REJEITADA", label: "Rejeitada" },
  { value: "CANCELADA", label: "Cancelada" },
];

const formatarData = (dataStr?: string | null) => {
  if (!dataStr) return "-";
  return new Date(dataStr).toLocaleString("pt-BR");
};

const getStatusClasse = (status: string) => {
  switch (status) {
    case "APROVADA":
      return "status-pill positive";
    case "REJEITADA":
      return "status-pill negative";
    case "CANCELADA":
      return "status-pill negative";
    default:
      return "status-pill pending";
  }
};

type LojaAgrupada = {
  idLoja: number;
  nomeLoja: string;
  itens: ManutencaoItem[];
};

type RegionalAgrupada = {
  idRegional: number | null;
  nomeRegional: string;
  lojas: LojaAgrupada[];
  totalItens: number;
};

function agruparPorRegional(lista: ManutencaoItem[]): RegionalAgrupada[] {
  const regionais = new Map<
    string,
    {
      idRegional: number | null;
      nomeRegional: string;
      lojas: Map<number, LojaAgrupada>;
    }
  >();

  for (const item of lista) {
    const idReg = item.idRegional;
    const keyReg = idReg != null ? String(idReg) : `__sem_regional__`;
    const nomeReg =
      item.nomeRegional?.trim() ||
      (idReg != null ? `Regional #${idReg}` : "Sem regional");

    let regEntry = regionais.get(keyReg);
    if (!regEntry) {
      regEntry = { idRegional: idReg, nomeRegional: nomeReg, lojas: new Map() };
      regionais.set(keyReg, regEntry);
    }

    const idLoja = item.id || item.idLoja; // Usa id caso idLoja venha nulo no objeto isolado
    const nomeLoja =
      item.nomeLoja?.trim() || item.nomeLoja?.trim() || `Loja #${idLoja}`;

    let lojaEntry = regEntry.lojas.get(idLoja);
    if (!lojaEntry) {
      lojaEntry = { idLoja, nomeLoja, itens: [] };
      regEntry.lojas.set(idLoja, lojaEntry);
    }
    lojaEntry.itens.push(item);
  }

  const regionaisOrdenadas: RegionalAgrupada[] = Array.from(regionais.values())
    .sort((a, b) => {
      if (a.idRegional == null) return 1;
      if (b.idRegional == null) return -1;
      return a.nomeRegional.localeCompare(b.nomeRegional, "pt-BR");
    })
    .map((reg) => {
      const lojasOrdenadas = Array.from(reg.lojas.values()).sort((a, b) =>
        a.nomeLoja.localeCompare(b.nomeLoja, "pt-BR"),
      );
      const totalItens = lojasOrdenadas.reduce(
        (acc, l) => acc + l.itens.length,
        0,
      );
      return {
        idRegional: reg.idRegional,
        nomeRegional: reg.nomeRegional,
        lojas: lojasOrdenadas,
        totalItens,
      };
    });

  return regionaisOrdenadas;
}

const PageManutencaoAgendada = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [itemAceitar, setItemAceitar] = useState<ManutencaoItem | null>(null);
  const [instrucoesCliente, setInstrucoesCliente] = useState("");

  const {
    lista,
    totalRegistros,
    pagina,
    tamanhoPagina,
    totalPaginas,
    statusFiltro,
    listaStatus,
    listaError,
    actionLoadingId,
  } = useSelector((state: RootState) => state.manutencao);

  const {
    listaEscopo,
    totalRegistrosEscopo,
    paginaEscopo,
    tamanhoPaginaEscopo,
    totalPaginasEscopo,
    listaEscopoStatus,
    listaEscopoError,
  } = useSelector((state: RootState) => state.manutencao);

  const [itemRejeitar, setItemRejeitar] = useState<ManutencaoItem | null>(null);
  const [justificativa, setJustificativa] = useState("");

  const [itemProrrogar, setItemProrrogar] = useState<ManutencaoItem | null>(
    null,
  );
  const [novoHorarioFim, setNovoHorarioFim] = useState("");

  const [itemApagar, setItemApagar] = useState<ManutencaoItem | null>(null);

  const [itemSuspender, setItemSuspender] = useState<ManutencaoItem | null>(
    null,
  );
  const [justificativaSuspensao, setJustificativaSuspensao] = useState("");

  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());

  const user = getUser();
  const userNivel = Number(user?.usuario?.nivel ?? 0);
  const userIdLoja = Number(user?.usuario?.idLoja ?? 0);
  const userIdRegional = Number(user?.usuario?.idRegional ?? 0);

  const usuarioRestrito = userNivel < NIVEL_MINIMO_ACOES;
  const podeGerenciarManutencao = userNivel > NIVEL_MINIMO_ACOES;

  const { lojaSelected } = useSelector((state: RootState) => state.getLojas); /// ajuste ao nome real do slice
  const { regionalSelected } = useSelector(
    (state: RootState) => state.getRegionais,
  ); /// ajuste ao nome real do slice

  const idLojaFiltro =
    !usuarioRestrito && (lojaSelected || 0) > 0 ? lojaSelected : null;
  const idRegionalFiltro =
    !usuarioRestrito && idLojaFiltro === null && (regionalSelected || 0) > 0
      ? regionalSelected
      : null;

  useEffect(() => {
    if (usuarioRestrito) {
      dispatch(
        listarManutencoes({
          pagina: paginaEscopo,
          tamanhoPagina: tamanhoPaginaEscopo,
          status: statusFiltro || undefined,
          idLoja: userIdLoja > 0 ? userIdLoja : null,
          idRegional:
            userIdLoja <= 0 && userIdRegional > 0 ? userIdRegional : null,
        }),
      );
    } else {
      dispatch(
        listarManutencoes({
          pagina,
          tamanhoPagina,
          status: statusFiltro || undefined,
          idLoja: idLojaFiltro,
          idRegional: idRegionalFiltro,
        }),
      );
    }
  }, [
    dispatch,
    usuarioRestrito,
    pagina,
    tamanhoPagina,
    paginaEscopo,
    tamanhoPaginaEscopo,
    statusFiltro,
    idLojaFiltro,
    idRegionalFiltro,
    userIdLoja,
    userIdRegional,
  ]);

  useEffect(() => {
    setCollapsedKeys(new Set());
  }, [statusFiltro, usuarioRestrito]);

  const listaAgrupada = useMemo(() => agruparPorRegional(lista), [lista]);

  const toggleCollapse = (key: string) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const podeProrrogar = (item: ManutencaoItem) =>
    item.status === "PENDENTE" || item.status === "APROVADA";

  const podeAceitarOuRejeitar = (item: ManutencaoItem) =>
    item.status === "PENDENTE";

  const handleAbrirAceitar = (item: ManutencaoItem) => {
    setItemAceitar(item);
    setInstrucoesCliente("");
  };

  const handleAbrirSuspender = (item: ManutencaoItem) => {
    setItemSuspender(item);
    setJustificativaSuspensao("");
  };

  const handleConfirmarSuspender = () => {
    if (!itemSuspender) return;
    dispatch(
      suspenderOcorrencia({
        id: itemSuspender.id,
        justificativa: justificativaSuspensao.trim(),
      }),
    );
    setItemSuspender(null);
  };

  const handlePausarSerie = (item: ManutencaoItem) => {
    dispatch(pausarSerie(item.id));
  };

  const handleRetomarSerie = (item: ManutencaoItem) => {
    dispatch(retomarSerie(item.id));
  };

  const handleConfirmarAceitar = () => {
    if (!itemAceitar || !instrucoesCliente.trim()) return;
    dispatch(
      aceitarManutencao({
        id: itemAceitar.id,
        instrucoesCliente: instrucoesCliente.trim(),
      }),
    );
    setItemAceitar(null);
  };

  const handleAbrirRejeitar = (item: ManutencaoItem) => {
    setItemRejeitar(item);
    setJustificativa("");
  };

  const handleConfirmarRejeitar = () => {
    if (!itemRejeitar || !justificativa.trim()) return;
    dispatch(
      rejeitarManutencao({
        id: itemRejeitar.id,
        justificativa: justificativa.trim(),
      }),
    );
    setItemRejeitar(null);
  };

  const handleAbrirProrrogar = (item: ManutencaoItem) => {
    setItemProrrogar(item);
    setNovoHorarioFim(item.horarioFim.slice(0, 16));
  };

  const handleConfirmarProrrogar = () => {
    if (!itemProrrogar || !novoHorarioFim) return;
    dispatch(
      prorrogarManutencao({ id: itemProrrogar.id, novoHorarioFim }),
    ).then(() => {
      if (usuarioRestrito) {
        dispatch(
          listarManutencoes({
            pagina: paginaEscopo,
            tamanhoPagina: tamanhoPaginaEscopo,
            status: statusFiltro || undefined,
            idLoja: userIdLoja > 0 ? userIdLoja : null,
            idRegional:
              userIdLoja <= 0 && userIdRegional > 0 ? userIdRegional : null,
          }),
        );
      } else {
        dispatch(
          listarManutencoes({
            pagina,
            tamanhoPagina,
            status: statusFiltro || undefined,
          }),
        );
      }
    });
    setItemProrrogar(null);
  };

  const handleAbrirApagar = (item: ManutencaoItem) => setItemApagar(item);

  const handleConfirmarApagar = () => {
    if (!itemApagar) return;
    dispatch(apagarManutencao(itemApagar.id));
    setItemApagar(null);
  };

  const isLoading = usuarioRestrito
    ? listaEscopoStatus === "loading"
    : listaStatus === "loading";

  const errorMessage = usuarioRestrito ? listaEscopoError : listaError;

  const listaAtual = usuarioRestrito ? listaEscopo : lista;

  const totalPaginasAtual = usuarioRestrito ? totalPaginasEscopo : totalPaginas;
  const paginaAtual = usuarioRestrito ? paginaEscopo : pagina;
  const totalRegistrosAtual = usuarioRestrito
    ? totalRegistrosEscopo
    : totalRegistros;

  const handleChangePagina = (novaPagina: number) => {
    if (usuarioRestrito) dispatch(setPaginaManutencaoEscopo(novaPagina));
    else dispatch(setPaginaManutencao(novaPagina));
  };

  const renderCard = (item: ManutencaoItem) => (
    <div key={item.id} className="maintenance-list-card">
      <div className="maintenance-list-card__header">
        <span className={getStatusClasse(item.status)}>{item.status}</span>
        <span className="maintenance-list-card__id">#{item.id}</span>
        {item.recorrente && (
          <span className="status-pill pending">
            Recorrente {item.serieAtiva ? "" : "(pausada)"}
          </span>
        )}
        {item.geradaAutomaticamente && (
          <span className="status-pill pending">Gerada automaticamente</span>
        )}
      </div>

      <h3 className="maintenance-list-card__cliente">
        {item.nomeLoja || item.cliente || item.nomeLoja || `Loja ${item.id}`}
      </h3>

      <div className="maintenance-list-card__info">
        <div>
          <span className="label">Início</span>
          <span className="value">{formatarData(item.horarioInicio)}</span>
        </div>
        <div>
          <span className="label">Fim</span>
          <span className="value">{formatarData(item.horarioFim)}</span>
        </div>
        <div>
          <span className="label">Tipo</span>
          <span className="value">{item.tipoManutencao || "-"}</span>
        </div>
        <div>
          <span className="label">Pontos Selecionados</span>
          <span className="value">
            {item.idsPontosSelecionados?.length > 0
              ? `${item.idsPontosSelecionados.length} ponto(s)`
              : "Loja inteira"}
          </span>
        </div>
      </div>

      {item.observacao && (
        <p className="maintenance-list-card__obs">{item.observacao}</p>
      )}

      {(item.status === "APROVADA" || item.status === "REJEITADA") &&
        item.nomeUsuarioAprovador && (
          <p className="maintenance-list-card__aprovador">
            <strong>
              {item.status === "APROVADA" ? "Aprovado por:" : "Rejeitada por:"}
            </strong>{" "}
            {item.nomeUsuarioAprovador} em {formatarData(item.dataAprovacao)}
          </p>
        )}

      {item.status === "CANCELADA" && item.justificativaCancelamento && (
        <p className="maintenance-list-card__rejeicao">
          <strong>Motivo do cancelamento:</strong>{" "}
          {item.justificativaCancelamento}
        </p>
      )}

      {/* NOVO — instruções pro cliente, visíveis pra qualquer usuário que veja o card */}
      {item.status === "APROVADA" && item.instrucoesCliente && (
        <p className="maintenance-list-card__instrucoes">
          <strong>Instruções para o cliente:</strong> {item.instrucoesCliente}
        </p>
      )}

      {item.status === "REJEITADA" && item.justificativaRejeicao && (
        <p className="maintenance-list-card__rejeicao">
          <strong>Motivo da rejeição:</strong> {item.justificativaRejeicao}
        </p>
      )}

      {podeGerenciarManutencao && (
        <div className="maintenance-list-card__actions">
          {podeAceitarOuRejeitar(item) && (
            <>
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleAbrirAceitar(item)}
                loading={actionLoadingId === item.id}
                disabled={actionLoadingId !== null}
              >
                Aceitar
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleAbrirRejeitar(item)}
                disabled={actionLoadingId !== null}
              >
                Rejeitar
              </Button>
            </>
          )}

          {podeProrrogar(item) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleAbrirProrrogar(item)}
              disabled={actionLoadingId !== null}
            >
              <Clock size={14} /> Prorrogar
            </Button>
          )}

          {item.recorrente && item.status === "APROVADA" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                item.serieAtiva
                  ? handlePausarSerie(item)
                  : handleRetomarSerie(item)
              }
              loading={actionLoadingId === item.id}
              disabled={actionLoadingId !== null}
            >
              {item.serieAtiva ? "Pausar série" : "Retomar série"}
            </Button>
          )}

          {item.geradaAutomaticamente && item.status === "APROVADA" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleAbrirSuspender(item)}
              disabled={actionLoadingId !== null}
            >
              Suspender
            </Button>
          )}

          <button
            type="button"
            className="btn-icon-danger"
            title="Apagar"
            onClick={() => handleAbrirApagar(item)}
            disabled={actionLoadingId !== null}
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <BaseLayout>
      <div className="maintenance-list-container">
        <div className="maintenance-list-header">
          <h2 className="maintenance-card__title">
            {usuarioRestrito ? "Minhas Manutenções" : "Manutenções Agendadas"}
          </h2>

          <div className="maintenance-list-filters">
            <label className="label label--sm">Status</label>
            <select
              title="Filtrar por status"
              value={statusFiltro}
              onChange={(e) =>
                dispatch(setStatusFiltroManutencao(e.target.value))
              }
            >
              {STATUS_OPCOES.map((opcao) => (
                <option key={opcao.value} value={opcao.value}>
                  {opcao.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {isLoading && <p className="paragraph paragraph--sm">Carregando...</p>}

        {errorMessage && (
          <div className="maintenance-form__error">{errorMessage}</div>
        )}

        {!isLoading && listaAtual.length === 0 && (
          <p className="paragraph paragraph--sm">
            Nenhuma manutenção encontrada para este filtro.
          </p>
        )}

        {/* ─── Visão AGRUPADA POR REGIONAL > LOJA (nível > 6) ─────────────── */}
        {!usuarioRestrito && !isLoading && lista.length > 0 && (
          <div className="maintenance-grouped-list">
            {listaAgrupada.map((regional) => {
              const regKey = `reg:${regional.idRegional ?? "null"}`;
              const regionalExpandida = !collapsedKeys.has(regKey);

              return (
                <div
                  key={regKey}
                  className="maintenance-group maintenance-group--regional"
                >
                  <button
                    type="button"
                    className="maintenance-group__header"
                    onClick={() => toggleCollapse(regKey)}
                  >
                    <ChevronDown
                      size={18}
                      className={`maintenance-group__chevron ${
                        regionalExpandida ? "is-open" : ""
                      }`}
                    />
                    <h3 className="maintenance-group__title">
                      {regional.nomeRegional}
                    </h3>
                    <span className="maintenance-group__count">
                      {regional.totalItens}{" "}
                      {regional.totalItens === 1
                        ? "solicitação"
                        : "solicitações"}
                    </span>
                  </button>

                  {regionalExpandida && (
                    <div className="maintenance-group__body">
                      {regional.lojas.map((loja) => {
                        const lojaKey = `${regKey}:loja:${loja.idLoja}`;
                        const lojaExpandida = !collapsedKeys.has(lojaKey);

                        return (
                          <div
                            key={lojaKey}
                            className="maintenance-group maintenance-group--loja"
                          >
                            <button
                              type="button"
                              className="maintenance-group__header maintenance-group__header--loja"
                              onClick={() => toggleCollapse(lojaKey)}
                            >
                              <ChevronDown
                                size={16}
                                className={`maintenance-group__chevron ${
                                  lojaExpandida ? "is-open" : ""
                                }`}
                              />
                              <h4 className="maintenance-group__title">
                                {loja.nomeLoja}
                              </h4>
                              <span className="maintenance-group__count">
                                {loja.itens.length}{" "}
                                {loja.itens.length === 1
                                  ? "solicitação"
                                  : "solicitações"}
                              </span>
                            </button>

                            {lojaExpandida && (
                              <div className="maintenance-group__body maintenance-group__body--loja">
                                {loja.itens.map(renderCard)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ─── Visão SIMPLES (nível < 6) ──────────────────────────────────── */}
        {usuarioRestrito && !isLoading && listaEscopo.length > 0 && (
          <div className="maintenance-list">{listaEscopo.map(renderCard)}</div>
        )}

        {/* ─── Paginação (funciona pra ambos os modos) ────────────────────── */}
        {totalPaginasAtual > 1 && (
          <div className="maintenance-list-pagination">
            <button
              type="button"
              onClick={() => handleChangePagina(paginaAtual - 1)}
              disabled={paginaAtual <= 1 || isLoading}
            >
              <ChevronLeft size={16} />
            </button>
            <span>
              Página {paginaAtual} de {totalPaginasAtual} ({totalRegistrosAtual}{" "}
              registros)
            </span>
            <button
              type="button"
              onClick={() => handleChangePagina(paginaAtual + 1)}
              disabled={paginaAtual >= totalPaginasAtual || isLoading}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {itemSuspender && (
        <Modal
          size="sm"
          header={
            <h3 className="heading heading--sm">
              Suspender Ocorrência #{itemSuspender.id}
            </h3>
          }
          callbackCancel={() => setItemSuspender(null)}
          actions={
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button size="sm" onClick={() => setItemSuspender(null)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleConfirmarSuspender}
              >
                Confirmar Suspensão
              </Button>
            </div>
          }
        >
          <div className="manutencao-modal">
            <label className="label label--sm" style={{ marginBottom: "6px" }}>
              Motivo (opcional)
            </label>
            <textarea
              value={justificativaSuspensao}
              onChange={(e) => setJustificativaSuspensao(e.target.value)}
              rows={3}
              placeholder="Ex: feriado, sem necessidade de manutenção neste dia..."
            />
          </div>
        </Modal>
      )}

      {itemAceitar && (
        <Modal
          size="sm"
          header={
            <h3 className="heading heading--sm">
              Aceitar Manutenção #{itemAceitar.id}
            </h3>
          }
          callbackCancel={() => setItemAceitar(null)}
          actions={
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button size="sm" onClick={() => setItemAceitar(null)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleConfirmarAceitar}
                disabled={!instrucoesCliente.trim()}
              >
                Confirmar Aceite
              </Button>
            </div>
          }
        >
          <div className="manutencao-modal">
            <label className="label label--sm" style={{ marginBottom: "6px" }}>
              Instruções para o Cliente *
            </label>
            <textarea
              value={instrucoesCliente}
              onChange={(e) => setInstrucoesCliente(e.target.value)}
              rows={4}
              placeholder="Ex: A manutenção será realizada entre 22h e 00h, sem necessidade de ação do cliente..."
            />
          </div>
        </Modal>
      )}

      {/* Modal: Rejeitar */}
      {itemRejeitar && (
        <Modal
          size="sm"
          header={
            <h3 className="heading heading--sm">
              Rejeitar Manutenção #{itemRejeitar.id}
            </h3>
          }
          callbackCancel={() => setItemRejeitar(null)}
          actions={
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button size="sm" onClick={() => setItemRejeitar(null)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleConfirmarRejeitar}
                disabled={!justificativa.trim()}
              >
                Confirmar Rejeição
              </Button>
            </div>
          }
        >
          <div className="manutencao-modal">
            <label className="label label--sm" style={{ marginBottom: "6px" }}>
              Justificativa *
            </label>
            <textarea
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              rows={4}
              placeholder="Explique o motivo da rejeição..."
            />
          </div>
        </Modal>
      )}

      {/* Modal: Prorrogar */}
      {itemProrrogar && (
        <Modal
          size="sm"
          header={
            <h3 className="heading heading--sm">
              Prorrogar Manutenção #{itemProrrogar.id}
            </h3>
          }
          callbackCancel={() => setItemProrrogar(null)}
          actions={
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button size="sm" onClick={() => setItemProrrogar(null)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleConfirmarProrrogar}
                disabled={!novoHorarioFim}
              >
                Confirmar Prorrogação
              </Button>
            </div>
          }
        >
          <div className="manutencao-modal">
            <p
              className="paragraph paragraph--sm"
              style={{ marginBottom: "10px" }}
            >
              Horário de fim atual:{" "}
              <strong>{formatarData(itemProrrogar.horarioFim)}</strong>
            </p>
            <label className="label label--sm" style={{ marginBottom: "6px" }}>
              Novo Horário de Fim *
            </label>
            <input
              type="datetime-local"
              value={novoHorarioFim}
              onChange={(e) => setNovoHorarioFim(e.target.value)}
            />
          </div>
        </Modal>
      )}

      {/* Modal: Apagar */}
      {itemApagar && (
        <Modal
          size="sm"
          header={
            <h3 className="heading heading--sm">
              Apagar Manutenção #{itemApagar.id}
            </h3>
          }
          callbackCancel={() => setItemApagar(null)}
          actions={
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button size="sm" onClick={() => setItemApagar(null)}>
                Cancelar
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleConfirmarApagar}
              >
                Confirmar Exclusão
              </Button>
            </div>
          }
        >
          <div className="manutencao-modal">
            <p className="paragraph paragraph--sm">
              Esta ação é permanente e não pode ser desfeita. Deseja realmente
              apagar esta manutenção?
            </p>
          </div>
        </Modal>
      )}
    </BaseLayout>
  );
};

export default PageManutencaoAgendada;
